import axios from '../../common/utils/interceptor';
import fetchBasicDetailsFromStorage from '../../common/utils/fetchBasicDetailsFromStorage';
import localizeContentMapper from '../../common/utils/localizeContentMapper';
import React, { useCallback, useEffect, useState } from 'react';
import redirectUrlOnInitialize from '../../common/utils/redirectUrlOnInitialize';
import { apiUrls, defaultBranding, routeMappings } from '../../constants';
import { appendTrackingScript } from '../../common/utils/appendThirdPartyScript';
import { FullPageLoader } from '../../common/components/FullPageLoader';
import { getExperienceInitCookie } from '../../common/utils/experienceInitCookie';
import { RoutePageProps } from './Routes';
import { useBasicDetails } from '../../store/storeApi';
import { useHistory, useLocation } from 'react-router-dom';
import { useQuery } from '../../common/hooks';
import { SessionStore } from '../../constants/sessionStore';
import { deleteCookie, getCookie, setCookie } from '../../common/utils/cookies';
import { storeProctoringConfig } from '../../proctoring/utils/proctoringConfig';
import { initiateTcPlusIntegratedFlow } from '../utils/initiateTcPlusIntegratedFlow';

interface BasicDetailsRequestPayload {
  link: string | null;
  assessmentCompletionURL?: string | null;
  assessmentNonCompletionURL?: string | null;
}

/**
 * - HOC Responsible to fetch basic data required during initialization
 * - Save the data in SessionStorage and make request when required
 * - Redirect to current WFI
 */
export const withBasicDetails = <P extends RoutePageProps>(
  Component: React.ComponentType<P>,
): ((props: P) => JSX.Element) => (props: P): JSX.Element => {
  const [isLoading, setLoading] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const query = useQuery();
  const { currentWorkflowItem, setBasicDetails } = useBasicDetails();
  const linkId = query.get('link');

  const tcPlusFlow = getCookie('TcPlusFlag') === '1';
  if (linkId) setCookie('LinkID', linkId);

  const expCookie = getExperienceInitCookie();
  const deadlineDatePassed = (expCookie && expCookie.candidateDeadlinePassed) || false;
  const isPreviewProject = (expCookie && expCookie.previewProject) || false;

  // Clear sessionStorage if the experience is re-initialised
  useEffect(() => {
    if (deadlineDatePassed) {
      history.push(routeMappings['EXPIRED_LINK']);
      return;
    }

    if (location.pathname.includes(routeMappings['INIT_EXPERIENCE'])) {
      const isUserLoggedOut = sessionStorage.getItem(SessionStore.loggedoutuser);
      const isUserLoggedOutDueToInactivity = sessionStorage.getItem(SessionStore.logoutDueToInactivity);
      if (!tcPlusFlow) {
        sessionStorage.clear();
        localStorage.clear();
      }

      deleteCookie('experience_url');
      if (isUserLoggedOut) {
        sessionStorage.setItem(SessionStore.loggedoutuser, isUserLoggedOut);
        if (isUserLoggedOutDueToInactivity) {
          sessionStorage.setItem(SessionStore.logoutDueToInactivity, isUserLoggedOutDueToInactivity);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Method to fetch following details by link id and save to store and sessionStorage:
   * - template and its settings
   * - current workflow item
   * - User details
   * Finally redirect to current workflow item
   */
  const fetchBasicDetailsByLinkId = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      const payload: BasicDetailsRequestPayload = {
        link: linkId,
      };

      if (tcPlusFlow) {
        payload['assessmentCompletionURL'] = sessionStorage.getItem(SessionStore.tpurlc) || null;
        payload['assessmentNonCompletionURL'] = sessionStorage.getItem(SessionStore.tpurlnc) || null;
      }

      const { data } = await axios.post(apiUrls.POST_EXPERIENCE_INIT, payload);

      sessionStorage.removeItem(SessionStore.tpurlc);
      sessionStorage.removeItem(SessionStore.tpurlnc);

      // Data transformed before saving to store
      // Fallback to default settings in case its null
      const parsedTemplateSettings = data.template.settings ? JSON.parse(data.template.settings) : defaultBranding;
      const templateSettings = localizeContentMapper(parsedTemplateSettings, data.template.localizedContentMap);

      const basicDetails = {
        ...data,
        template: {
          ...data.template,
          settings: templateSettings,
        },
      };

      // Append third party tracking script for specific company
      if (data.workflowSession) appendTrackingScript(data.workflowSession.companyId);

      setBasicDetails(basicDetails);
      setLoading(false);

      Object.entries(basicDetails).forEach(([key, value]) => {
        sessionStorage.setItem(key as SessionStore, JSON.stringify(value));
      });

      // on initial API call if the usersession is active and project is not preview, link is integrted
      if (data.userSessionActive && !isPreviewProject && !tcPlusFlow) {
        sessionStorage.setItem(SessionStore.isIntegrated, 'true');
      }

      if (tcPlusFlow && expCookie?.autoLogin) {
        const url = await initiateTcPlusIntegratedFlow();
        url && history.push(url);
        return;
      }

      if (!location.pathname.includes(routeMappings['FEEDBACK'])) {
        history.push(redirectUrlOnInitialize(basicDetails));
      }
    } catch (error) {
      setLoading(false);
      console.error('Fetch experience init error: ', error);
      if (error && error.data && error.data.message) {
        console.error(error.data.message);
        history.push(routeMappings['NOT_FOUND']);
      }
    }
    if (!tcPlusFlow) storeProctoringConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkId, history]);

  /**
   * Method to go to next workflow item
   *  - Pass the current workflow item id as query params,
   *  - get next workflow item details
   *  - update the sessionStorage & store to redirect to next workflow item
   */
  const getNextWorkflowItem = async (): Promise<void> => {
    if (currentWorkflowItem.lastItem) return;
    const params = { workflowItem: currentWorkflowItem.id };
    try {
      const { data } = await axios.post(apiUrls.POST_NEXT_WORKFLOW_ITEM, null, { params });
      if (data) {
        sessionStorage.setItem(SessionStore.currentWorkflowItem, JSON.stringify(data));
        if (data.capturesPii) {
          history.push(routeMappings['LOGIN']);
        } else {
          history.push(`${routeMappings[data.code]}/${data.id}`);
        }
      } else {
        history.push(routeMappings['NOT_FOUND']);
      }
    } catch (error) {
      console.error('error getting next wfItem', error);
      history.push(routeMappings['NOT_FOUND']);
    }
  };

  useEffect(() => {
    /** Fetch basic details from server if not present in session storage */
    if (sessionStorage.getItem(SessionStore.currentWorkflowItem) && sessionStorage.getItem(SessionStore.template)) {
      const basicDetails = fetchBasicDetailsFromStorage();
      setBasicDetails(basicDetails);

      const routesOutsideWorkflow = [
        routeMappings['USER_PROFILE'],
        routeMappings['SSO'],
        routeMappings['DPN'],
        routeMappings['WELCOME'],
        routeMappings['APPLICATION_FORM'],
        routeMappings['RE_ATTEMPT'],
        routeMappings['RESEARCH_QUESTIONS'],
        routeMappings['EEO'],
        routeMappings['OFCCP'],
        routeMappings['YOUR_RESPONSIBILITY'],
        routeMappings['TASK_COMPLETION'],
        routeMappings['CANDIDATE_CONSENT'],
        routeMappings['PROCTORING_SETUP'],
        `${routeMappings['TASK']}/`,
        `${routeMappings['MFS']}/`,
        routeMappings['TASKLIST_HURDLE_FAIL'],
        routeMappings['TASKLIST_END'],
        routeMappings['BASICTASKLIST_END'],
        routeMappings['VIDEO_FEEDBACK'],
        routeMappings['FEEDBACK'],
        routeMappings['BIODATA'],
        routeMappings['INFO'],
      ];
      if (!routesOutsideWorkflow.some((route) => location.pathname.includes(route))) {
        history.push(redirectUrlOnInitialize(basicDetails));
      }
    } else if (linkId || tcPlusFlow) {
      fetchBasicDetailsByLinkId();
    } else {
      // TODO: If we need to auto-initialize the workflow session
      history.push(routeMappings['NOT_FOUND']);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkId, history, location.pathname, fetchBasicDetailsByLinkId]);

  return (
    <>
      <FullPageLoader isLoading={isLoading} />
      <Component {...props} currentWorkflowItem={currentWorkflowItem} getNextWorkflowItem={getNextWorkflowItem} />
    </>
  );
};
